import React, { useState, useEffect, useRef } from "react";
import {
  DndContext,
  useDraggable,
  useDroppable,
  closestCenter,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { rectSortingStrategy, SortableContext } from "@dnd-kit/sortable";

import { api_without_cancellation } from "../../../api/api";
import { formatCalendarDate, getCaseId } from "../../../Utils/helper";

const CasesPinnedView = ({ pinnedCases, setPinnedCases }) => {
  const caseId = getCaseId();
  const sensors = useSensors(useSensor(MouseSensor), useSensor(TouchSensor));

  // Refs for measuring different sections
  const nameRefs = useRef({});
  const caseTypeRefs = useRef({});
  const dateRefs = useRef({});

  const [maxWidths, setMaxWidths] = useState({
    names: 0,
    caseTypes: 0,
    dates: 0,
  });

  useEffect(() => {
    const calculateMaxWidths = () => {
      const nameWidths = Object.values(pinnedCases).map((caseItem) => {
        if (!caseItem?.for_client) return 0;
        const fullName = `${caseItem.for_client.last_name}, ${caseItem.for_client.first_name}`;
        return fullName.length * 9.2;
      });

      const caseTypeWidths = Object.values(pinnedCases).map((caseItem) => {
        if (!caseItem?.case_type?.name) return 0;
        return caseItem.case_type.name.length * 9.1;
      });

      const dateWidths = Object.values(pinnedCases).map((caseItem) => {
        if (!caseItem?.incident_date) return 0;
        return caseItem.incident_date.length * 9.1;
      });

      setMaxWidths({
        names: Math.max(...nameWidths, 175),
        caseTypes: Math.max(...caseTypeWidths, 120),
        dates: Math.max(...dateWidths, 120),
      });
    };

    calculateMaxWidths();

    window.addEventListener("resize", calculateMaxWidths);
    return () => window.removeEventListener("resize", calculateMaxWidths);
  }, [pinnedCases]);

  const updatePinnedCases = async (position, position_to, caseId_to, caseId_from) => {
    let requestData = {};

    if (caseId_from?.id === caseId_to?.id && position_to) {
      requestData = {
        [position]: null,
        [position_to]: caseId_to?.id,
      };
    } else if (position_to) {
      requestData = {
        [position]: caseId_from?.id ? caseId_from?.id : null,
        [position_to]: caseId_to?.id,
      };
    } else {
      requestData = {
        [position]: pinnedCases[position]?.id === Number(caseId) ? null : caseId,
      };
    }
    try {
      await api_without_cancellation.post("/api/pinned-cases/", requestData).then((response) => {
        setPinnedCases(response.data);
      });
    } catch (error) {
      console.error("error", error);
    }
  };

  // const handleDragEnd = (event) => {
  //   const { active, over } = event;

  //   if (over && active.id !== over.id) {
  //     const updatedCases = { ...pinnedCases };

  //     const temp = updatedCases[active.id];

  //     updatedCases[active.id] = updatedCases[over.id];

  //     updatedCases[over.id] = temp;

  //     setPinnedCases(updatedCases);
  //     updatePinnedCases(active.id, over.id, updatedCases[over.id], updatedCases[active.id]);
  //   } else if (!over) {
  //     updatePinnedCases(active.id);
  //   }
  // };

  const handleDragEnd = (event) => {
    const { active, over, delta } = event;
    // Check if it was a click (minimal movement)
    const wasClick = Math.abs(delta.x) < 5 && Math.abs(delta.y) < 5;
    if (wasClick && pinnedCases[active.id]) {
      // It was a click on a pinned case - unpin it
      updatePinnedCases(active.id);
    } else if (over && active.id !== over.id) {
      // It was a drag to swap positions
      const updatedCases = { ...pinnedCases };
      const temp = updatedCases[active.id];
      updatedCases[active.id] = updatedCases[over.id];
      updatedCases[over.id] = temp;

      setPinnedCases(updatedCases);
      updatePinnedCases(active.id, over.id, updatedCases[over.id], updatedCases[active.id]);
    } else if (!over && !wasClick) {
      // It was dragged outside (and not just a click)
      updatePinnedCases(active.id);
    }
  };

  const renderCaseItem = (position) => (
    <PinnedCaseItem
      key={position}
      id={position}
      pinnedCase={pinnedCases[position]}
      maxWidths={maxWidths}
      pinnedCaseFn={updatePinnedCases}
      currentCaseId={caseId}
      refs={{
        nameRef: (el) => (nameRefs.current[position] = el),
        caseTypeRef: (el) => (caseTypeRefs.current[position] = el),
        dateRef: (el) => (dateRefs.current[position] = el),
      }}
    />
  );

  return (
    <div className="cases-pinned-view-container">
      <div className="cases-pinned-body">
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={Object.keys(pinnedCases)} strategy={rectSortingStrategy}>
            <div className="pinned-case-grid">{["p1", "p2"].map(renderCaseItem)}</div>
            <div className="pinned-case-grid">{["p3", "p4"].map(renderCaseItem)}</div>
            <div className="pinned-case-grid">{["p5", "p6"].map(renderCaseItem)}</div>
          </SortableContext>
        </DndContext>
      </div>
    </div>
  );
};

const PinnedCaseItem = React.forwardRef(
  ({ id, pinnedCase, maxWidths, refs, pinnedCaseFn, currentCaseId }, ref) => {
    const { setNodeRef: setDroppableRef } = useDroppable({ id });
    const {
      setNodeRef: setDraggableRef,
      listeners,
      attributes,
      isDragging,
      transform,
    } = useDraggable({ id });
    return (
      <div
        ref={setDroppableRef}
        className={`case-item case-item-${id} ${!pinnedCase ? "empty-case-item" : ""}`}
        style={{
          transform: transform ? `translate(${transform.x}px, ${transform.y}px)` : "",
          opacity: isDragging ? 0.5 : 1,
        }}
      >
        <div
          ref={(el) => {
            setDraggableRef(el);
            if (typeof ref === "function") ref(el);
            else if (ref) ref.current = el;
          }}
          {...(pinnedCase ? listeners : {})}
          {...(pinnedCase ? attributes : {})}
          className="case-content"
        >
          {pinnedCase ? (
            <div className="d-flex" style={{ minHeight: "42px", position: "relative" }}>
              <div
                ref={refs.nameRef}
                className="d-flex align-items-center content-section"
                style={{
                  width: `${maxWidths.names}px`,
                  marginRight: "5px",
                  position: "relative",
                  zIndex: 2,
                }}
              >
                <span className="ic ic-avatar ic-19 has-avatar-icon has-cover-img">
                  {pinnedCase?.for_client?.profile_pic_19p && (
                    <img
                      className="output-3 theme-ring"
                      src={pinnedCase.for_client.profile_pic_19p}
                      alt="Client Profile"
                    />
                  )}
                </span>
                <span
                  className="m-l-5 text-black text-black-2 whitespace-nowrap"
                  style={{ fontWeight: "600" }}
                >
                  {pinnedCase?.for_client
                    ? `${pinnedCase.for_client.last_name}, ${pinnedCase.for_client.first_name}`
                    : ""}
                </span>
              </div>
              <div className="d-flex flex-column" style={{ position: "relative", zIndex: 2 }}>
                <div
                  ref={refs.caseTypeRef}
                  className="d-flex align-items-center content-section"
                  style={{ width: `${maxWidths.caseTypes}px` }}
                >
                  <span className="ic ic-15 d-flex align-items-center">
                    {pinnedCase?.case_type?.casetype_icon && (
                      <img src={pinnedCase.case_type.casetype_icon} alt="Case Type Icon" />
                    )}
                  </span>
                  <span
                    className="m-l-5 text-black text-black-2 whitespace-nowrap"
                    style={{ fontWeight: "600" }}
                  >
                    {pinnedCase?.case_type ? pinnedCase.case_type.name : ""}
                  </span>
                </div>
                <div
                  ref={refs.dateRef}
                  className="d-flex align-items-center content-section"
                  style={{ width: `${maxWidths.dates}px` }}
                >
                  <span className="ic ic-15 ic-incident d-flex align-items-center" />
                  <span className="text-black m-l-5" style={{ fontWeight: "600" }}>
                    {formatCalendarDate(pinnedCase?.incident_date)}
                  </span>
                </div>
              </div>

              <span
                className="text-center d-flex align-items-center justify-content-center"
                style={{
                  width: `${maxWidths.names + maxWidths.caseTypes + 5}px`,
                  color: "var(--primary-25)",
                  fontWeight: "600",
                  position: "absolute",
                  top: "10px",
                  opacity: "0.3",
                  zIndex: 1,
                }}
              >
                CASE PINNED TO HOME # {id.slice(1)}
              </span>
            </div>
          ) : (
            <div
              className="d-flex"
              style={{
                width: `${maxWidths.names + maxWidths.caseTypes + 5}px`,
                height: "42px",
              }}
              onClick={() => {
                pinnedCaseFn(id, null, { id: currentCaseId }, null);
              }}
            >
              <span
                className="text-center d-flex align-items-center justify-content-center"
                style={{
                  width: `${maxWidths.names + maxWidths.caseTypes + 5}px`,
                  color: "var(--primary-25)",
                  fontWeight: "600",
                }}
              >
                PIN CASE TO HOME # {id.slice(1)}
              </span>
            </div>
          )}
        </div>
      </div>
    );
  }
);

export default CasesPinnedView;
